import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { buildRoutePlan } from '../core/planner/routePlanner'
import type { CoordinateCalibration, GameState } from '../types/game'

function releaseMapUrls(game: GameState | null) {
  for (const map of game?.maps ?? []) {
    if (map.objectUrl) URL.revokeObjectURL(map.objectUrl)
  }
}

export const useGameStore = defineStore('game', () => {
  const game = ref<GameState | null>(null)
  const sourceFileName = ref<string | null>(null)

  const loaded = computed(() => game.value !== null)
  const player = computed(() => game.value?.player ?? null)
  const quests = computed(() => game.value?.quests ?? [])
  const outposts = computed(() => game.value?.outposts ?? [])
  const resources = computed(() => game.value?.resources ?? [])
  const maps = computed(() => game.value?.maps ?? [])
  const route = computed(() => game.value?.route ?? null)
  const readyQuestCount = computed(
    () => quests.value.filter((quest) => quest.objectivesMet).length
  )
  const missingResourceCount = computed(
    () => resources.value.filter((resource) => resource.missing > 0).length
  )

  function setGame(data: GameState, fileName: string) {
    releaseMapUrls(game.value)
    game.value = data
    sourceFileName.value = fileName
  }

  function setCalibration(calibration: CoordinateCalibration) {
    if (!game.value) return

    const index = game.value.calibrations.findIndex(
      (item) => item.biome === calibration.biome
    )
    if (index === -1) game.value.calibrations.push(calibration)
    else game.value.calibrations[index] = calibration

    game.value.route = buildRoutePlan({
      player: game.value.player,
      quests: game.value.quests,
      outposts: game.value.outposts,
      resources: game.value.resources,
      calibrations: game.value.calibrations
    })
  }

  function reset() {
    releaseMapUrls(game.value)
    game.value = null
    sourceFileName.value = null
  }

  return {
    game,
    loaded,
    sourceFileName,
    player,
    quests,
    outposts,
    resources,
    maps,
    route,
    readyQuestCount,
    missingResourceCount,
    setGame,
    setCalibration,
    reset
  }
})
